/**
 * Form chrome tokens — card-style custom form groups, inputs, journal tables.
 * Derived from active MUI theme (App / Form canvas WYSIWYG).
 */
import { useMemo } from 'react'
import { useTheme, alpha, createTheme, type Theme } from '@mui/material/styles'
import type { AppTheme } from '../../../types/theme.types'
import { MONO_FONT } from '@/shared/typography'

export interface FormChromeTokens {
  cardBg: string
  cardBorder: string
  headerBg: string
  accentHeaderBg: string
  accentTitle: string
  muted: string
  title: string
  inputBg: string
  outline: string
  outlineHover: string
  focus: string
  /** Operator selection ring — primary accent on canvas blocks */
  selectionRing: string
  selectionGlow: string
  prefixBg: string
  debit: string
  credit: string
  cardShadow: string
  uiFont: string
  monoFont: string
  radiusCard: number
  gap: number
  pillRadius: number
  buttonHeight: number
}

declare module '@mui/material/styles' {
  interface Palette {
    formChrome?: FormChromeTokens
  }
  interface PaletteOptions {
    formChrome?: FormChromeTokens
  }
}

export function buildFormChromeTokens(theme: Theme): FormChromeTokens {
  const p = theme.palette
  const isDark = p.mode === 'dark'
  return {
    cardBg: p.background.paper,
    cardBorder: p.divider,
    headerBg: isDark ? alpha(p.primary.main, 0.14) : alpha(p.primary.main, 0.06),
    accentHeaderBg: isDark ? alpha(p.success.main, 0.18) : alpha(p.success.main, 0.1),
    accentTitle: isDark ? p.success.light : p.success.dark,
    muted: p.text.secondary,
    title: p.text.primary,
    inputBg: isDark ? alpha(p.background.paper, 0.85) : p.background.paper,
    outline: p.divider,
    outlineHover: isDark ? alpha(p.common.white, 0.24) : alpha(p.common.black, 0.2),
    focus: p.primary.main,
    selectionRing: p.primary.main,
    selectionGlow: alpha(p.primary.main, 0.18),
    prefixBg: isDark ? alpha(p.primary.main, 0.14) : alpha(p.primary.main, 0.08),
    debit: p.info.main,
    credit: p.success.main,
    cardShadow: theme.shadows[isDark ? 3 : 1],
    uiFont: String(theme.typography.fontFamily ?? '"Inter", system-ui, sans-serif'),
    monoFont:
      (theme.typography as { fontFamilyMonospace?: string }).fontFamilyMonospace ??
      MONO_FONT,
    radiusCard: 16,
    gap: 8,
    pillRadius: 999,
    buttonHeight: 32,
  }
}

/** Build tokens from designer AppTheme (used in appThemeToMui). */
export function formChromeFromAppTheme(appTheme: AppTheme): FormChromeTokens {
  const isDark = appTheme.mode === 'dark'
  const p = appTheme.palette
  const s = appTheme.semantic
  const primary = p.primary[500]
  const success = p.success[500]
  const successContrast = isDark ? p.success[300] : p.success[700]
  return {
    cardBg: s.surfaceBackground,
    cardBorder: s.borderDefault,
    headerBg: isDark ? alpha(primary, 0.14) : alpha(primary, 0.06),
    accentHeaderBg: isDark ? alpha(success, 0.18) : alpha(success, 0.1),
    accentTitle: successContrast,
    muted: s.textSecondary,
    title: s.textPrimary,
    inputBg: s.surfaceBackground,
    outline: s.borderDefault,
    outlineHover: s.borderStrong,
    focus: primary,
    selectionRing: primary,
    selectionGlow: alpha(primary, 0.18),
    prefixBg: isDark ? alpha(primary, 0.14) : alpha(primary, 0.08),
    debit: p.info[500],
    credit: success,
    cardShadow: appTheme.shape?.shadowSm ?? appTheme.shape?.shadowMd ?? 'none',
    uiFont: appTheme.typography.fontFamily,
    monoFont: appTheme.typography.monoFontFamily,
    radiusCard: 16,
    gap: 8,
    pillRadius: 999,
    buttonHeight: 32,
  }
}

export function useFormChrome(): FormChromeTokens {
  const theme = useTheme()
  return useMemo(
    () => theme.palette.formChrome ?? buildFormChromeTokens(theme),
    [theme],
  )
}

/** Attach formChrome to a theme returned from appThemeToMui. */
export function withFormChromePalette(base: Theme): Theme {
  return createTheme(base, {
    palette: {
      formChrome: buildFormChromeTokens(base),
    },
  })
}
